import { useState, useEffect, useRef } from 'react'

const KEY = 'apav-a11y'

const defaults = {
  fontSize: 0,        // 0 normal · 1 grande · 2 maior
  contrast: false,
  grayscale: false,
  highlightLinks: false,
  pauseAnimations: false,
}

const FONT_SIZES = ['Normal', 'Grande', 'Maior']

const TOGGLES = [
  { key: 'contrast',        label: 'Alto contraste' },
  { key: 'grayscale',       label: 'Escala de cinzas' },
  { key: 'highlightLinks',  label: 'Destacar links' },
  { key: 'pauseAnimations', label: 'Pausar animações' },
]

export default function AccessibilityWidget({ open, onClose }) {
  const [s, setS] = useState(() => {
    try {
      return { ...defaults, ...JSON.parse(localStorage.getItem(KEY)) }
    } catch {
      return defaults
    }
  })

  const panelRef = useRef(null)
  const closeBtnRef = useRef(null)

  /* Apply settings to <html> */
  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(s))
    const html = document.documentElement
    const sizes = ['', '112%', '125%']
    html.style.fontSize = sizes[s.fontSize] || ''
    html.classList.toggle('a11y-contrast', s.contrast)
    html.classList.toggle('a11y-grayscale', s.grayscale)
    html.classList.toggle('a11y-highlight-links', s.highlightLinks)
    html.classList.toggle('a11y-pause-animations', s.pauseAnimations)
  }, [s])

  /* Keyboard accessibility: Close on Escape key */
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    setTimeout(() => {
      closeBtnRef.current?.focus()
    }, 50)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onClose])

  const toggle = (key) => setS(prev => ({ ...prev, [key]: !prev[key] }))
  const reset  = () => setS(defaults)

  if (!open) return null

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Painel de Opções de Acessibilidade"
      aria-modal="true"
      className="a11y-panel shadow-2xl"
    >
      {/* Header */}
      <div className="a11y-panel__header">
        <span className="a11y-panel__title" id="a11y-title">Acessibilidade</span>
        <button
          ref={closeBtnRef}
          onClick={onClose}
          aria-label="Fechar painel de acessibilidade"
          className="a11y-panel__close focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1"
        >
          ✕
        </button>
      </div>

      {/* Content */}
      <div className="a11y-panel__body">

        {/* Font size */}
        <div className="a11y-panel__section">
          <span className="a11y-panel__label" id="font-size-label">Tamanho do texto</span>
          <div className="a11y-panel__row" role="group" aria-labelledby="font-size-label">
            {FONT_SIZES.map((label, i) => (
              <button
                key={i}
                onClick={() => setS(prev => ({ ...prev, fontSize: i }))}
                className={`a11y-panel__step${s.fontSize === i ? ' active' : ''} focus-visible:ring-2 focus-visible:ring-brand-500`}
                aria-pressed={s.fontSize === i}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Toggles */}
        <div className="a11y-panel__section mt-2">
          <span className="a11y-panel__label">Opções visuais e de interação</span>
          <div className="flex flex-col gap-2">
            {TOGGLES.map(({ key, label }) => (
              <button
                key={key}
                role="switch"
                aria-checked={s[key]}
                onClick={() => toggle(key)}
                className={`a11y-panel__toggle${s[key] ? ' active' : ''} focus-visible:ring-2 focus-visible:ring-brand-500`}
              >
                <span className="a11y-panel__toggle-dot" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="a11y-panel__footer">
        <button
          onClick={reset}
          className="a11y-panel__reset hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-brand-500"
          aria-label="Repor todas as opções de acessibilidade para os valores normais"
        >
          Repor tudo
        </button>
      </div>
    </div>
  )
}
